import React from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { cn } from '@/lib/utils/cn'
import { useToastStore, type ToastType } from '@/lib/toast/toastStore'
import { undoRegistry } from '@/lib/toast/undo'
import { X, CheckCircle, AlertTriangle, Info, AlertCircle, Undo2 } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Toast — Radix Toast.Provider subscribed to toastStore              */
/*  Slide/fade motion per §17.9                                        */
/* ------------------------------------------------------------------ */

const typeConfig: Record<ToastType, { icon: React.ElementType; className: string }> = {
  success: { icon: CheckCircle, className: 'border-success-500 text-success-500' },
  error: { icon: AlertCircle, className: 'border-error-500 text-error-500' },
  warning: { icon: AlertTriangle, className: 'border-warning-500 text-warning-500' },
  info: { icon: Info, className: 'border-info-500 text-info-500' },
}

function ToastItem({ id, type, title, description, durationMs }: {
  id: string
  type: ToastType
  title: string
  description?: string
  durationMs: number
}) {
  const remove = useToastStore((s) => s.remove)
  const config = typeConfig[type]
  const Icon = config.icon
  const undoFn = undoRegistry.get(id)

  return (
    <ToastPrimitive.Root
      duration={durationMs}
      onOpenChange={(open) => {
        if (!open) remove(id)
      }}
      className={cn(
        'group pointer-events-auto relative flex w-full items-start gap-3',
        'overflow-hidden rounded-lg border bg-white p-4 shadow-lg',
        'data-[state=open]:animate-in data-[state=open]:slide-in-from-top-full data-[state=open]:fade-in-0',
        'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right-full data-[state=closed]:fade-out-0',
        'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
        'data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform',
        'data-[swipe=end]:animate-out data-[swipe=end]:slide-out-to-right-full',
        config.className,
      )}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <ToastPrimitive.Title className="text-sm font-semibold text-neutral-900">
          {title}
        </ToastPrimitive.Title>
        {description && (
          <ToastPrimitive.Description className="mt-0.5 text-sm text-neutral-500">
            {description}
          </ToastPrimitive.Description>
        )}
        {undoFn && (
          <button
            type="button"
            onClick={() => void undoFn()}
            className="mt-1.5 inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-primary-600 transition-colors hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <Undo2 className="h-3 w-3" aria-hidden="true" />
            Ångra
          </button>
        )}
      </div>
      <ToastPrimitive.Close
        aria-label="Stäng"
        className="shrink-0 rounded p-1 text-neutral-400 hover:text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
      >
        <X className="h-4 w-4" />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  )
}

export function ToastViewport() {
  return (
    <ToastPrimitive.Viewport
      aria-live="polite"
      aria-label="Notifieringar"
      className={cn(
        'fixed top-4 right-4 z-[100] flex max-h-screen w-full max-w-sm flex-col gap-2',
        'pointer-events-none [&>*]:pointer-events-auto',
      )}
    />
  )
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const messages = useToastStore((s) => s.messages)

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {children}
      {messages.map((msg) => (
        <ToastItem key={msg.id} {...msg} />
      ))}
      <ToastViewport />
    </ToastPrimitive.Provider>
  )
}

ToastProvider.displayName = 'ToastProvider'
ToastViewport.displayName = 'ToastViewport'
